import React, { useState } from 'react';
import Table from './components/Table';
import { Box, Breadcrumbs, Typography } from '@mui/material';
import Detail from './components/Detail';
import { Link, useParams } from 'react-router-dom';
import { ROUTE_STRING } from 'constants/RouteString';
import ListLayout from '@tenant/core/App/ListLayout';
import AppSearch from '@tenant/core/AppSearchBar';
import { useDebounce } from '@tenant/utility/hooks';
import useAppsQuery from './hooks/useAppsQuery';
import {
  DEFAULT_PAGE_INDEX,
  DEFAULT_PAGE_SIZE,
} from '@tenant/utility/constants/default';
import AppPagination from '@tenant/core/App/Pagination/AppPagination';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { LoadingButton } from '@mui/lab';
import useExportListApp from './hooks/useExportListApp';
import AllowedAccess from '@tenant/core/AppPermission/AllowedAccess';
import { AuthorizePermission } from '@tenant/utility/constants/authorizePermission';

const ListApp = () => {
  const { appId } = useParams();
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [pageIndex, setPageIndex] = useState(DEFAULT_PAGE_INDEX);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const { data, isLoading } = useAppsQuery({
    searchTerm: debouncedSearchTerm,
    pageIndex,
    pageSize: pageSize,
  });
  const exportMutate = useExportListApp();

  const clearSearchTerm = () => {
    setSearchTerm('');
  };

  const onChangeSearch = (e) => {
    setSearchTerm(e.target.value);
    setPageIndex(DEFAULT_PAGE_INDEX);
  };

  const onPageChange = (value) => {
    setPageIndex(value);
  };

  return (
    <ListLayout
      rows={data?.Items?.length ?? 0}
      isShowDetail={!!appId}
      title={
        <Breadcrumbs aria-label='breadcrumb'>
          <Typography color='text.primary'>Developer</Typography>
          <Typography color='text.primary'>
            <Link to={ROUTE_STRING.LIST_APP.LIST}>Apps</Link>
          </Typography>
          {appId && <Typography color='text.primary'>{appId}</Typography>}
        </Breadcrumbs>
      }
      table={<Table data={data} isLoading={isLoading} />}
      detail={<Detail />}
      appHeader={
        <Box
          flex={1}
          display='flex'
          justifyContent='space-between'
          alignItems='center'
        >
          <Box
            display='flex'
            justifyContent='space-between'
            alignItems='center'
          >
            <AppSearch
              isClearable
              searchValue={searchTerm}
              onClearSearch={clearSearchTerm}
              iconPosition='right'
              id='outlined-adornment-weight'
              size='small'
              overlap={false}
              onChange={onChangeSearch}
            ></AppSearch>
          </Box>
          <AllowedAccess permissionIds={[AuthorizePermission.APP_EXPORT]}>
            <LoadingButton
              variant='contained'
              loading={exportMutate.isPending}
              startIcon={<FileDownloadIcon />}
              onClick={() => {
                exportMutate.mutate();
              }}
            >
              Export
            </LoadingButton>
          </AllowedAccess>
        </Box>
      }
      appFooter={
        <Box justifyContent={'center'} className='footerContainer'>
          <AppPagination
            loading={isLoading}
            isChangePageSize
            totalCount={data?.TotalCount || 0}
            pageIndex={pageIndex}
            pageSize={pageSize}
            onPageChange={onPageChange}
            onPageSizeChange={(e, value) => {
              setPageSize(value?.value ?? DEFAULT_PAGE_SIZE);
              setPageIndex(DEFAULT_PAGE_INDEX);
            }}
          />
        </Box>
      }
    />
  );
};

export default React.memo(ListApp);
